import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ModalTitle } from 'react-bootstrap';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { useVisualizacao } from '../../VisualizacaoContext';
import { FaRegEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';

const EditDescriptionContainer = styled.div<{
  optionSelected: boolean;
  isEditing: boolean;
}>`
  min-width: 579.09px;
  padding: 13px ${({ isEditing }) => (isEditing ? '8px' : '2px')};
  border-radius: 3.5px;
  transition: background-color 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;

  background-color: ${({ isEditing }) =>
    isEditing ? 'rgba(235, 235, 235, 0.75)' : 'transparent'};

  &:hover {
    background-color: ${({ optionSelected }) =>
      optionSelected ? 'rgba(235, 235, 235, 0.75)' : 'transparent'};
    cursor: ${({ optionSelected }) => (optionSelected ? 'pointer' : 'default')};
  }

  height: 2.5rem;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

const TitleInput = styled.input`
  font-size: 1.28571429rem;
  font-weight: 500;
  line-height: 1.42857143;
  color: #172b4d;
  background-color: transparent;
  border: none;
  width: 100%;
  padding-right: 3.75rem;
  outline: none;
`;

const ButtonGroup = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  gap: 4px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 1.2rem;

  &:hover {
    opacity: 0.7;
  }
`;

export const EditableModalTitle: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    selectedOptionView,
    handleChangeView,
    optionsViews,
    fixedColumns,
    setOptionsViews,
    setIsChangeView,
    btnSalvarBlock,
    setBtnSalvarBlock,
  } = useVisualizacao();

  useEffect(() => {
    if (selectedOptionView) {
      setTempValue(selectedOptionView.label);
    }
  }, [selectedOptionView]);

  const handleContainerClick = () => {
    if (selectedOptionView) {
      setIsEditing(true);
      setTempValue(selectedOptionView?.label);
    }
  };

  const handleConfirm = useCallback(async () => {
    if (selectedOptionView) {
      const finalValue = tempValue.trimStart().trimEnd().toUpperCase();

      if (!finalValue || finalValue.length === 0 || finalValue === '') {
        toast.warning('A descrição deve ser informada');
        return;
      }

      const newSelected = {
        label: finalValue,
        value: selectedOptionView.value,
        qtd_colunas_fixadas: fixedColumns,
        qtd_registro: 10,
      };

      await handleChangeView(newSelected, true);

      const updatedOptions = optionsViews.filter(
        (opt) => opt.value !== selectedOptionView.value,
      );

      setOptionsViews([...updatedOptions, newSelected]);
      setIsEditing(false);
    }
  }, [
    fixedColumns,
    handleChangeView,
    optionsViews,
    selectedOptionView,
    setOptionsViews,
    tempValue,
  ]);

  const handleCancel = () => {
    if (selectedOptionView) {
      setTempValue(selectedOptionView?.label);
      setIsEditing(false);
      setBtnSalvarBlock(false);
    }
  };

  const handleClickOutside = useCallback(
    async (event: MouseEvent) => {
      if (!isEditing) return;

      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        await handleConfirm();
      }
    },
    [handleConfirm, isEditing],
  );

  useEffect(() => {
    if (isEditing) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) setBtnSalvarBlock(true);
    if (e.target.value.length <= 50) {
      const cursorPosition = e.target.selectionStart;

      const newValue = e.target.value.trimStart();
      setTempValue(newValue.toUpperCase());

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = cursorPosition;
          inputRef.current.selectionEnd = cursorPosition;
        }
      }, 0);
    }
  };

  const handleInputWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsChangeView(false);
  };

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        await handleConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleConfirm]);

  return (
    <EditDescriptionContainer
      optionSelected={!!selectedOptionView}
      onClick={handleContainerClick}
      isEditing={isEditing}
    >
      {isEditing && (
        <InputWrapper ref={wrapperRef} onClick={handleInputWrapperClick}>
          <TitleInput
            value={tempValue}
            onChange={handleChange}
            maxLength={50}
            autoFocus
            ref={inputRef}
            onFocus={() => {
              setIsChangeView(false);
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsChangeView(false);
            }}
          />
          <ButtonGroup>
            <IconButton
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                await handleConfirm();
              }}
              disabled={!selectedOptionView || btnSalvarBlock}
            >
              <AiOutlineCheck color="#28a745" />
            </IconButton>
            <IconButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              <AiOutlineClose color="#dc3545" />
            </IconButton>
          </ButtonGroup>
        </InputWrapper>
      )}
      {!isEditing && (
        <ModalTitle>
          {selectedOptionView?.label.toUpperCase()}{' '}
          <FaRegEdit
            color="#949494df"
            size={18}
            style={{ marginTop: '-0.3125rem', marginLeft: '0.3125rem' }}
          />{' '}
        </ModalTitle>
      )}
    </EditDescriptionContainer>
  );
};
